// Third-party Analytics Integration
// Supports: Google Analytics 4 (GA4), Google Tag Manager (GTM), Google Search Console (GSC)

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

// Initialize Google Analytics 4
export const initGA4 = (measurementId: string) => {
  if (typeof window === 'undefined') return;

  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() {
    window.dataLayer?.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false, // We'll send manually
  });
};

// Initialize Google Tag Manager
export const initGTM = (containerId: string) => {
  if (typeof window === 'undefined') return;

  // Load GTM script
  const script = document.createElement('script');
  script.innerHTML = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${containerId}');
  `;
  document.head.appendChild(script);

  // Add noscript iframe
  const noscript = document.createElement('noscript');
  noscript.innerHTML = `
    <iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}"
    height="0" width="0" style="display:none;visibility:hidden"></iframe>
  `;
  document.body.insertBefore(noscript, document.body.firstChild);
};

// Track page view
export const trackPageView = (url: string, title?: string) => {
  if (typeof window === 'undefined') return;

  // GA4
  if (window.gtag) {
    window.gtag('event', 'page_view', {
      page_path: url,
      page_title: title || document.title,
    });
  }

  // GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'page_view',
      page_path: url,
      page_title: title || document.title,
    });
  }
};

// Track custom event
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window === 'undefined') return;

  // GA4
  if (window.gtag) {
    window.gtag('event', eventName, eventParams);
  }

  // GTM
  if (window.dataLayer) {
    window.dataLayer.push({
      event: eventName,
      ...eventParams,
    });
  }
};

// Track user properties
export const setUserProperties = (properties: Record<string, any>) => {
  if (typeof window === 'undefined') return;

  if (window.gtag) {
    window.gtag('set', 'user_properties', properties);
  }
};

// Track conversion
export const trackConversion = (
  conversionId: string,
  value?: number,
  currency: string = 'INR'
) => {
  if (typeof window === 'undefined') return;

  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: conversionId,
      value: value,
      currency: currency,
    });
  }
};

// E-commerce tracking
export const trackPurchase = (
  transactionId: string,
  value: number,
  items: any[],
  currency: string = 'INR'
) => {
  if (typeof window === 'undefined') return;

  if (window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: transactionId,
      value: value,
      currency: currency,
      items: items,
    });
  }

  if (window.dataLayer) {
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        transaction_id: transactionId,
        value: value,
        currency: currency,
        items: items,
      },
    });
  }
};

// Track pet view
export const trackPetView = (petId: string, petName: string, breed: string, price?: number) => {
  trackEvent('view_item', {
    item_id: petId,
    item_name: petName,
    item_category: breed,
    price: price,
  });
};

// Track breeder view
export const trackBreederView = (breederId: string, breederName: string) => {
  trackEvent('view_breeder', {
    breeder_id: breederId,
    breeder_name: breederName,
  });
};

// Track search
export const trackSearch = (searchTerm: string, filters?: Record<string, any>) => {
  trackEvent('search', {
    search_term: searchTerm,
    ...filters,
  });
};

// Track inquiry
export const trackInquiry = (petId: string, breederId: string) => {
  trackEvent('generate_lead', {
    pet_id: petId,
    breeder_id: breederId,
  });
};

// Track signup
export const trackSignup = (method: string = 'email') => {
  trackEvent('sign_up', {
    method: method,
  });
};

// Track login
export const trackLogin = (method: string = 'email') => {
  trackEvent('login', {
    method: method,
  });
};

// Track favorite
export const trackFavorite = (itemType: string, itemId: string) => {
  trackEvent('add_to_wishlist', {
    item_type: itemType,
    item_id: itemId,
  });
};

// Facebook Pixel integration
export const initFacebookPixel = (pixelId: string) => {
  if (typeof window === 'undefined') return;

  const script = document.createElement('script');
  script.innerHTML = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);

  const noscript = document.createElement('noscript');
  noscript.innerHTML = `
    <img height="1" width="1" style="display:none"
    src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/>
  `;
  document.body.appendChild(noscript);
};

// Track Facebook event
export const trackFBEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window === 'undefined') return;
  
  const fbq = (window as any).fbq;
  if (fbq) {
    fbq('track', eventName, params);
  }
};

// Microsoft Clarity integration
export const initClarity = (projectId: string) => {
  if (typeof window === 'undefined') return;

  const script = document.createElement('script');
  script.innerHTML = `
    (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${projectId}");
  `;
  document.head.appendChild(script);
};

// Hotjar integration
export const initHotjar = (siteId: string) => {
  if (typeof window === 'undefined') return;

  const script = document.createElement('script');
  script.innerHTML = `
    (function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:${siteId},hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
  `;
  document.head.appendChild(script);
};

// Initialize all analytics based on config
export const initializeAnalytics = (config: {
  ga4MeasurementId?: string;
  gtmContainerId?: string;
  fbPixelId?: string;
  clarityProjectId?: string;
  hotjarSiteId?: string;
}) => {
  if (config.ga4MeasurementId) {
    initGA4(config.ga4MeasurementId);
  }

  if (config.gtmContainerId) {
    initGTM(config.gtmContainerId);
  }

  if (config.fbPixelId) {
    initFacebookPixel(config.fbPixelId);
  }

  if (config.clarityProjectId) {
    initClarity(config.clarityProjectId);
  }

  if (config.hotjarSiteId) {
    initHotjar(config.hotjarSiteId);
  }
};
